import { useEffect, useState } from "react";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import UsagePanel from "@/components/UsagePanel";
import { PROFILE } from "@/constants/testIds";

const STEPS = [
  {
    title: "Aktifkan billing di Google Cloud",
    body: "Generate image dari Gemini tidak gratis, jadi project Google kamu wajib terhubung ke billing. Buka Google Cloud Console > Billing, klik Create account, tambahkan kartu kredit/debit, lalu hubungkan project ke billing account tersebut. Tips: set Budget alert supaya kamu dapat email kalau mendekati kuota.",
    link: "https://console.cloud.google.com/billing/create",
    cta: "Buka pengaturan billing",
  },
  {
    title: "Buka Google AI Studio dan pilih project",
    body: "Masuk ke AI Studio dengan akun Google yang sama. Di pojok kiri atas, pilih project yang billing-nya sudah aktif. Kalau daftar project kosong, buat project baru di AI Studio, lalu aktifkan billing untuk project itu.",
    link: "https://aistudio.google.com/apikey",
    cta: "Buka AI Studio",
  },
  {
    title: "Buat API key",
    body: "Klik Create API key, pilih project tadi, lalu salin key yang muncul. Format key terbaru diawali AQ. (contoh: AQ.Ab8RN6...). Key hanya ditampilkan sekali, jadi langsung simpan di Notepad dulu supaya tidak hilang.",
  },
  {
    title: "Paste key di kolom Gemini API key",
    body: "Tempel key ke kolom di atas, lalu klik Save & verify key. Aplikasi akan memverifikasi key ke Google. Kalau gagal, akan muncul alasannya (mis. billing belum aktif). Kalau berhasil, status jadi hijau: Connected.",
  },
  {
    title: "Mulai generate",
    body: "Semua generate memakai key kamu sendiri. Biaya ditagih Google ke project milikmu, bukan ke pemilik aplikasi: sekitar $0,067 per gambar, jadi 6 foto sekitar $0,40.",
  },
  {
    title: "Kalau key bermasalah",
    body: "Key ditolak (401/403) biasanya karena billing belum aktif atau key dicabut. Buka lagi AI Studio, buat key baru, lalu paste di kolom yang sama untuk mengganti. Key lama bisa dihapus kapan saja lewat tombol Remove key.",
  },
  {
    title: "Key aman",
    body: "Key disimpan terenkripsi (Fernet) di database, tidak pernah ditampilkan lagi setelah disimpan, dan hanya dipakai untuk generate milik akun kamu. Hanya 4 karakter terakhir yang terlihat sebagai pengingat.",
  },
];

export default function Profile() {
  const { user, refresh } = useAuth();
  const [apiKey, setApiKey] = useState("");
  const [showKey, setShowKey] = useState(false);
  const [keyState, setKeyState] = useState({ status: "idle", message: "" });
  const [license, setLicense] = useState({ activated: false, code: "" });
  const [licenseState, setLicenseState] = useState({ status: "idle", message: "" });
  const [code, setCode] = useState("");

  const loadLicense = () =>
    api
      .get("/settings/license")
      .then((r) =>
        setLicense({
          activated: Boolean(r.data?.activated),
          code: r.data?.code || "",
        }),
      )
      .catch(() => {});

  useEffect(() => {
    loadLicense();
  }, []);

  const activate = async (event) => {
    event.preventDefault();
    setLicenseState({ status: "loading", message: "" });
    try {
      const response = await api.post("/settings/license/activate", { code });
      setCode("");
      setLicense({ activated: true, code: response.data?.code || "" });
      setLicenseState({ status: "success", message: response.data?.message || "Kode aktif." });
      await refresh();
    } catch (requestError) {
      setLicenseState({
        status: "error",
        message:
          requestError.response?.data?.detail?.message ||
          requestError.response?.data?.detail ||
          "Kode tidak bisa dipakai.",
      });
    }
  };

  if (!user) return null;

  const saveApiKey = async (event) => {
    event.preventDefault();
    if (!apiKey.trim()) {
      setKeyState({ status: "error", message: "Paste your Gemini API key first." });
      return;
    }
    setKeyState({ status: "loading", message: "Verifying with Google…" });
    try {
      const response = await api.post("/settings/gemini-key", { api_key: apiKey });
      setApiKey("");
      await refresh();
      setKeyState({
        status: "success",
        message: response.data?.message || "API key connected.",
      });
    } catch (requestError) {
      setKeyState({
        status: "error",
        message:
          requestError.response?.data?.detail?.message ||
          requestError.response?.data?.detail ||
          "Unable to verify the key.",
      });
    }
  };

  const deleteApiKey = async () => {
    setKeyState({ status: "loading", message: "" });
    try {
      await api.delete("/settings/gemini-key");
      await refresh();
      setKeyState({
        status: "success",
        message: "Key removed. Generation is locked until you connect a key.",
      });
    } catch (requestError) {
      setKeyState({
        status: "error",
        message: requestError.response?.data?.detail || "Unable to remove API key.",
      });
    }
  };

  return (
    <div className="max-w-3xl mx-auto px-6 lg:px-10 py-12 lg:py-16">
      <p className="font-mono text-[11px] uppercase tracking-[0.3em] text-zinc-500">Account</p>
      <h1 className="mt-3 font-serif text-4xl sm:text-5xl leading-none tracking-tight text-zinc-950">
        Profile
      </h1>

      <div
        data-testid={PROFILE.info}
        className="mt-10 border border-zinc-200 bg-white p-6 flex items-center gap-5"
      >
        {user.picture ? (
          <img src={user.picture} alt="" className="h-16 w-16 rounded-full object-cover" />
        ) : (
          <div className="h-16 w-16 rounded-full bg-zinc-200 flex items-center justify-center font-serif text-2xl">
            {(user.name || user.email).charAt(0).toUpperCase()}
          </div>
        )}
        <div>
          <p className="font-serif text-2xl text-zinc-950">{user.name}</p>
          <p className="text-[13px] text-zinc-500">{user.email}</p>
          <p className="mt-1 font-mono text-[10px] uppercase tracking-[0.25em] text-zinc-400">
            Google · {user.is_admin ? "Administrator" : "Member"}
          </p>
        </div>
      </div>

      <div className="mt-6">
        <UsagePanel />
      </div>

      <div className="mt-6 border border-zinc-200 bg-white p-6">
        <div className="flex items-center justify-between gap-4">
          <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-zinc-500">
            Activation code
          </p>
          {license.activated ? (
            <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-emerald-700">
              Active {license.code}
            </span>
          ) : (
            <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-rose-600">
              Required to use the app
            </span>
          )}
        </div>

        {license.activated ? (
          <p className="mt-3 text-sm text-zinc-600">
            Your account is activated. One code can only ever activate one account, so
            this access cannot be shared or resold.
          </p>
        ) : (
          <>
            <p className="mt-3 text-sm text-zinc-600">
              Paste the activation code we sent after your purchase. Format:
              <span className="font-mono"> APS-XXXX-XXXX-XXXX</span>. One code = one
              account.
            </p>
            <p className="mt-4 text-[12px] text-zinc-400">
              Belum punya kode? Hubungi penjual untuk mendapatkan akses.
            </p>
            <form onSubmit={activate} className="mt-4 flex flex-col gap-3 sm:flex-row">
              <input
                value={code}
                onChange={(event) => setCode(event.target.value.toUpperCase())}
                placeholder="APS-XXXX-XXXX-XXXX"
                autoComplete="off"
                className="w-full border border-zinc-300 px-4 py-3 font-mono text-sm uppercase tracking-wider outline-none focus:border-zinc-950"
              />
              <button
                type="submit"
                disabled={licenseState.status === "loading" || !code.trim()}
                className="shrink-0 bg-zinc-950 px-5 py-3 text-sm text-[#FBFBF9] hover:bg-zinc-800 disabled:opacity-60"
              >
                {licenseState.status === "loading" ? "Activating…" : "Activate"}
              </button>
            </form>
            {licenseState.message && (
              <p
                className={`mt-3 text-sm ${
                  licenseState.status === "error" ? "text-rose-600" : "text-emerald-700"
                }`}
              >
                {licenseState.message}
              </p>
            )}
            <p className="mt-3 text-[11px] text-zinc-400">
              Tidak punya kode? Hubungi penjual. Satu kode hanya bisa dipakai satu akun,
              jadi tidak bisa dibagikan.
            </p>
          </>
        )}
      </div>

      <div className="mt-6 border border-zinc-200 bg-white p-6">
        <div className="flex items-center justify-between gap-4">
          <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-zinc-500">
            Gemini API key
          </p>
          {user.has_byok ? (
            <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-emerald-700">
              Connected {user.byok_hint}
            </span>
          ) : (
            <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-rose-600">
              Required to generate
            </span>
          )}
        </div>

        {user.has_byok ? (
          <>
            <p className="mt-3 text-sm text-zinc-600">
              Your own Google API key is active. Every generation is billed by Google to
              your project, so you pay the provider directly.
            </p>
            <div className="mt-4 flex flex-wrap gap-3">
              <button
                type="button"
                onClick={deleteApiKey}
                disabled={keyState.status === "loading"}
                className="border border-zinc-300 px-4 py-2 text-sm hover:border-zinc-950 disabled:opacity-60"
              >
                Remove key
              </button>
              <a
                href="https://aistudio.google.com/apikey"
                target="_blank"
                rel="noreferrer"
                className="border border-zinc-300 px-4 py-2 text-sm hover:border-zinc-950"
              >
                Manage keys in AI Studio
              </a>
            </div>
          </>
        ) : (
          <>
            <p className="mt-3 text-sm text-zinc-600">
              Generation is locked until you connect your own Gemini API key. This way
              every image you create is billed by Google to your account, not to the app
              owner. It takes about two minutes — follow the steps below.
            </p>
            <a
              href="#gemini-tutorial"
              onClick={(event) => {
                event.preventDefault();
                document
                  .getElementById("gemini-tutorial")
                  ?.scrollIntoView({ behavior: "smooth", block: "start" });
              }}
              className="mt-4 inline-flex bg-zinc-950 px-4 py-2 text-[13px] text-[#FBFBF9] hover:bg-zinc-800"
            >
              Lihat tutorial lengkap ↓
            </a>
          </>
        )}

        <form onSubmit={saveApiKey} className="mt-5 space-y-3">
          <div className="flex gap-2">
            <input
              type={showKey ? "text" : "password"}
              value={apiKey}
              onChange={(event) => setApiKey(event.target.value)}
              placeholder={user.has_byok ? "Paste a new key to replace the current one" : "Paste your Gemini API key (AQ.…)"}
              autoComplete="off"
              className="w-full border border-zinc-300 px-4 py-3 text-sm outline-none focus:border-zinc-950"
            />
            <button
              type="button"
              onClick={() => setShowKey((value) => !value)}
              className="border border-zinc-300 px-3 text-[11px] uppercase tracking-wide hover:border-zinc-950"
            >
              {showKey ? "Hide" : "Show"}
            </button>
          </div>
          {keyState.message && (
            <p
              className={`text-sm ${
                keyState.status === "error" ? "text-rose-600" : "text-emerald-700"
              }`}
            >
              {keyState.message}
            </p>
          )}
          <button
            type="submit"
            disabled={keyState.status === "loading"}
            className="bg-zinc-950 px-5 py-3 text-sm text-[#FBFBF9] hover:bg-zinc-800 disabled:opacity-60"
          >
            {keyState.status === "loading" ? "Verifying…" : "Save & verify key"}
          </button>
        </form>
        <p className="mt-3 text-[11px] text-zinc-400">
          Key disimpan terenkripsi di database dan hanya dipakai untuk generate milik akun
          ini.
        </p>
      </div>

      <div className="mt-6 border border-zinc-200 bg-white p-6" id="gemini-tutorial">
        <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-zinc-500">
          Tutorial: hubungkan Gemini API key
        </p>
        <p className="mt-3 text-sm text-zinc-600">
          Tujuh langkah, sekitar 3 menit. Only perlu sekali — setelah itu semua generate
          memakai key dan billing kamu sendiri.
        </p>
        <ol className="mt-5 space-y-3">
          {STEPS.map((step, index) => (
            <li key={step.title} className="border border-zinc-200 p-4">
              <div className="flex items-start gap-3">
                <span className="mt-0.5 font-mono text-[11px] text-zinc-400">
                  {String(index + 1).padStart(2, "0")}
                </span>
                <div className="flex-1">
                  <p className="text-[13px] font-medium text-zinc-900">{step.title}</p>
                  <p className="mt-1.5 text-[13px] leading-relaxed text-zinc-600">
                    {step.body}
                  </p>
                  {step.link && (
                    <a
                      href={step.link}
                      target="_blank"
                      rel="noreferrer"
                      className="mt-3 inline-flex border border-zinc-300 px-4 py-2 text-[13px] hover:border-zinc-950"
                    >
                      {step.cta} →
                    </a>
                  )}
                </div>
              </div>
            </li>
          ))}
        </ol>
        <div className="mt-4 border-l-2 border-amber-500 pl-3 text-[12px] text-zinc-600">
          <p className="font-medium text-zinc-800">Catatan penting</p>
          <p className="mt-1">
            Tidak ada API key gratis untuk generate image dari Google. Kalau project kamu
            belum punya billing, key akan ditolak dengan error 403. Aktifkan billing dulu
            (Langkah 1), baru buat key-nya.
          </p>
        </div>
      </div>

      <div className="mt-6 border border-zinc-200 bg-white p-6">
        <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-zinc-500">
          Account
        </p>
        <dl className="mt-4 grid gap-3 text-[13px]">
          <div className="flex justify-between gap-4 border-b border-zinc-100 pb-2">
            <dt className="text-zinc-500">Name</dt>
            <dd className="text-zinc-900">{user.name}</dd>
          </div>
          <div className="flex justify-between gap-4 border-b border-zinc-100 pb-2">
            <dt className="text-zinc-500">Email</dt>
            <dd className="text-zinc-900">{user.email}</dd>
          </div>
          <div className="flex justify-between gap-4 border-b border-zinc-100 pb-2">
            <dt className="text-zinc-500">Role</dt>
            <dd className="text-zinc-900">{user.is_admin ? "Administrator" : "Member"}</dd>
          </div>
          <div className="flex justify-between gap-4">
            <dt className="text-zinc-500">Billing</dt>
            <dd className="text-zinc-900">
              {user.has_byok ? "Your own Gemini API key" : "Not connected"}
            </dd>
          </div>
        </dl>
      </div>
    </div>
  );
}
